import { CheckCircleIcon } from '@heroicons/react/outline'
import StrategyCallButton from './StrategyCallButton'

const COLORS = {
    dark: '#1F2937',
    accent: '#ED5C2F',
    accentAlt: '#F28230',
    yellow: '#FDB515',
    white45: 'rgba(255,255,255,0.45)',
    white55: 'rgba(255,255,255,0.55)',
    white60: 'rgba(255,255,255,0.6)',
    white10: 'rgba(255,255,255,0.1)',
    white05: 'rgba(255,255,255,0.05)',
    text50: 'color-mix(in oklab, #1f2937 50%, transparent)',
    text70: 'color-mix(in oklab, #1f2937 70%, transparent)',
    text80: 'color-mix(in oklab, #1f2937 80%, transparent)',
    light: '#FFF8EA',
}

const defaultContent = {
    number: '05',
    eyebrow: 'For High-Volume Mailers',
    titlePrefix: 'Serious volume, ',
    titleHighlight: 'serious terms.',
    description:
        "If direct mail is a core channel for your business, off-the-shelf pricing and shared production capacity won't cut it. We work with high-volume mailers under annual agreements - with committed volume, contract pricing, and dedicated capacity to keep your programs moving.",
    includedHeading: "What's included",
    includedItems: [
        'Annual agreement with committed volume',
        'Special pricing with built-in growth tiers',
        'Dedicated production capacity',
        'Priority turnaround & SLAs',
        'Dedicated account team',
        'Platform access with tracking',
        'SDK & API access available',
        'White-label & reseller options',
    ],
    cta: {
        label: 'Talk to our team',
        href: '/contact-us',
    },
    pipeline: {
        label: 'Production Pipeline',
        live: 'Live',
        total: '147,901',
        subtitle: 'Pieces in production this week',
        bars: [
            { height: '48%', color: '#F28230' },
            { height: '62%', color: '#F28230' },
            { height: '55%', color: '#F28230' },
            { height: '78%', color: '#F28230' },
            { height: '71%', color: '#F28230' },
            { height: '88%', color: '#ED5C2F' },
            { height: '95%', color: '#ED5C2F' },
            { height: '100%', color: '#ED5C2F' },
            { height: '82%', color: '#ED5C2F' },
            { height: '90%', color: '#ED5C2F' },
            { height: '96%', color: '#ED5C2F' },
            { height: '84%', color: '#ED5C2F' },
        ],
        stats: [
            { label: 'Active programs', value: '38', color: '#ffffff' },
            { label: 'Drops this week', value: '12', color: '#FDB515' },
            { label: 'On schedule', value: '100%', color: '#ffffff' },
        ],
    },
}

function IncludedItem({ children }) {
    return (
        <div className="flex items-start gap-2">
            <CheckCircleIcon
                className="mt-0.5 h-5 w-5 shrink-0"
                style={{ color: COLORS.accent }}
            />
            <div className="text-sm leading-7" style={{ color: COLORS.text80 }}>
                {children}
            </div>
        </div>
    )
}

export default function HighVolumeProgramsSection(props) {
    const number = props?.number || defaultContent.number
    const eyebrow = props?.eyebrow || defaultContent.eyebrow
    const titlePrefix = props?.titlePrefix || defaultContent.titlePrefix
    const titleHighlight =
        props?.titleHighlight || defaultContent.titleHighlight
    const description = props?.description || defaultContent.description
    const includedHeading =
        props?.includedHeading || defaultContent.includedHeading
    const includedItems =
        Array.isArray(props?.includedItems) && props.includedItems.length > 0
            ? props.includedItems
            : defaultContent.includedItems
    const cta = props?.cta || defaultContent.cta
    const pipeline = props?.pipeline || defaultContent.pipeline

    return (
        <section id="high-volume-programs" className="bg-white scroll-mt-24">
            <div className="container mx-auto px-4 py-16 lg:py-24">
                <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
                    <div className="flex flex-col gap-5">
                        <div className="flex items-center gap-3">
                            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#ed5c2f]">
                                <div className="text-sm font-bold leading-7 text-white">
                                    {number}
                                </div>
                            </div>
                            <div
                                className="text-xs uppercase tracking-[1.2px]"
                                style={{ color: COLORS.text50 }}>
                                {eyebrow}
                            </div>
                        </div>

                        <h2
                            className="text-3xl font-[700] tracking-tight md:text-[44px] md:leading-[1.1]"
                            style={{ color: COLORS.dark }}>
                            {titlePrefix}
                            <span style={{ color: COLORS.accent }}>
                                {titleHighlight}
                            </span>
                        </h2>

                        <p
                            className="text-lg leading-7"
                            style={{ color: COLORS.text70 }}>
                            {description}
                        </p>

                        <div
                            className="mt-2 rounded-2xl border p-6"
                            style={{
                                backgroundColor: COLORS.light,
                                borderColor:
                                    'color-mix(in oklab, #ed5c2f 15%, transparent)',
                            }}>
                            <div
                                className="mb-3 text-xs font-bold uppercase tracking-[1.2px] leading-7"
                                style={{ color: COLORS.accent }}>
                                {includedHeading}
                            </div>
                            <div className="grid grid-cols-1 gap-x-6 gap-y-2 lg:grid-cols-2">
                                {includedItems.map((item, index) => (
                                    <IncludedItem key={`${item}-${index}`}>
                                        {item}
                                    </IncludedItem>
                                ))}
                            </div>
                        </div>

                        {cta?.label ? (
                            <div>
                                <StrategyCallButton className="inline-flex w-fit rounded-full bg-rtl-gradient px-6 py-3.5 text-base font-semibold text-white shadow-lg transition hover:opacity-95">
                                    {cta.label}
                                </StrategyCallButton>
                            </div>
                        ) : null}
                    </div>

                    <div className="flex flex-col gap-5 rounded-3xl bg-[#1f2937] p-7 shadow-xl transition-shadow duration-300 hover:shadow-2xl">
                        <div className="flex items-center justify-between">
                            <div
                                className="text-xs font-semibold uppercase tracking-[1.2px] leading-7"
                                style={{ color: COLORS.white60 }}>
                                {pipeline.label}
                            </div>
                            <div
                                className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1"
                                style={{
                                    backgroundColor: COLORS.white05,
                                    borderColor: COLORS.white10,
                                }}>
                                <div className="h-1.5 w-1.5 rounded-full bg-[#22c55e] animate-pulse" />
                                <div className="text-xs font-semibold leading-7 text-white">
                                    {pipeline.live}
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col">
                            <div className="text-5xl font-bold tracking-tight text-white tabular-nums">
                                {pipeline.total}
                            </div>
                            <div
                                className="mt-1 text-sm leading-7"
                                style={{ color: COLORS.white55 }}>
                                {pipeline.subtitle}
                            </div>
                        </div>

                        <div
                            className="flex h-24 items-end gap-1.5 border-b pb-3"
                            style={{ borderColor: COLORS.white10 }}>
                            {pipeline.bars.map((bar, index) => (
                                <div
                                    key={`${bar.height}-${index}`}
                                    className="flex-1 rounded-t transition-opacity duration-200 hover:opacity-70"
                                    style={{
                                        height: bar.height,
                                        backgroundColor: bar.color,
                                    }}
                                />
                            ))}
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                            {pipeline.stats.map((stat) => (
                                <div
                                    key={stat.label}
                                    className="flex flex-col gap-1">
                                    <div
                                        className="text-xs font-semibold uppercase tracking-[1.2px] leading-7"
                                        style={{ color: COLORS.white45 }}>
                                        {stat.label}
                                    </div>
                                    <div
                                        className="text-xl font-bold leading-7"
                                        style={{ color: stat.color }}>
                                        {stat.value}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
